import { useCallback, useMemo, useState } from 'react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import debounce from 'lodash.debounce'
import { useQuery } from '@apollo/client'
import { useLingui } from '@lingui/react/macro'

import ComboBox, { InputProps as ComboBoxProps } from '@/components/ui/ComboBox'
import usePaginatedSpaces, { QueryVariables } from '../hooks/usePaginatedSpaces'

export type SelectSpaceProps = Omit<ComboBoxProps, 'onChange'> & {
  id?: number | null
  onChange: (id: number | null) => void
}

export default function SelectSpace({
  id,
  onChange,
  ...rest
}: SelectSpaceProps) {
  const { t } = useLingui()
  const [search, setSearch] = useState<string>('')
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')

  const variables = useMemo<QueryVariables>(
    () => ({
      filter: {
        search: debouncedSearch
      },
      sort: {
        direction: GraphQL.SortDirection.Asc,
        field: GraphQL.SpaceSortField.Identifier
      }
    }),
    [debouncedSearch]
  )

  const { data } = useQuery(AdminAPI.space.GET, {
    variables: { id: id ?? 0 },
    skip: !id
  })

  const space = useMemo(
    () => getFragment(SharedAPI.space.SPACE_FRAGMENT, data?.getSpace),
    [data]
  )

  const updateDebouncedSearch = useMemo(
    () => debounce(setDebouncedSearch, 500),
    [setDebouncedSearch]
  )

  const onSearch = useCallback(
    (newValue: string) => {
      setSearch(newValue)
      updateDebouncedSearch(newValue)
    },
    [setSearch, updateDebouncedSearch]
  )

  const { items: spaces, loading } = usePaginatedSpaces(variables)

  const items = useMemo(() => {
    const items = (spaces ?? []).map(space => {
      return {
        value: `${space.id}`,
        label: `${space.identifier} (${space.name})`
      }
    })

    if (space && !items.some(item => item.value === `${space.id}`)) {
      items.push({
        value: `${space.id}`,
        label: `${space.identifier} (${space.name})`
      })
    }

    return items
  }, [spaces, space])

  return (
    <ComboBox
      onChange={value => {
        const id = parseInt(value)
        onChange(isNaN(id) ? null : id)
      }}
      options={items}
      search={search}
      onChangeSearch={onSearch}
      loading={loading}
      searchText={t`Search Rooms`}
      {...rest}
      value={id != null ? `${id}` : ''}
    />
  )
}
